import { useCallback, useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'

import {
    DeviceDetectionFilters,
    DeviceDistributionOptionsResponse,
    DeviceDistributionResponse,
    DeviceMosquito,
    PaginatedDetectionResponse,
} from '../types/device-mosquito.ts'
import api from '../utils/axiosConfig'

const CACHE_TTL = 60_000
const DISTRIBUTION_VIEWPORT_PRECISION = 4
const DEVICE_DISTRIBUTION_STATS_REBUILDING_CODE =
    'device_distribution_stats_rebuilding'
let cachedRecords: DeviceMosquito[] | null = null
let lastFetchTime = 0
const distributionCache = new Map<
    string,
    { data: DeviceDistributionResponse; fetchedAt: number }
>()
const distributionRequests = new Map<string, Promise<DeviceDistributionResponse>>()
const deviceDetectionRequests = new Map<string, Promise<PaginatedDetectionResponse>>()
let cachedDistributionOptions: DeviceDistributionOptionsResponse | null = null
let distributionOptionsFetchedAt = 0
let distributionOptionsRequest: Promise<DeviceDistributionOptionsResponse> | null = null
let distributionOptionsForceRequest: Promise<DeviceDistributionOptionsResponse> | null =
    null

export interface DeviceDistributionQuery {
    minLng?: number
    maxLng?: number
    minLat?: number
    maxLat?: number
    zoom?: number
    projectIds?: number[]
    regionIds?: number[]
    deviceIds?: string[]
    pestTypes?: string[]
}

export interface UseDistributionOptionsConfig {
    initialForceRefresh?: boolean
    initialLoad?: boolean
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

export const isDeviceDistributionStatsRebuildingError = (
    error: unknown
): boolean => {
    if (!isAxiosError(error)) {
        return false
    }
    const detail = error.response?.data?.detail
    return (
        isObjectRecord(detail) &&
        detail.code === DEVICE_DISTRIBUTION_STATS_REBUILDING_CODE
    )
}

const getDeviceDistributionErrorMessage = (): string =>
    '无法加载设备分布数据'

const getDeviceDistributionOptionsErrorMessage = (): string =>
    '无法加载设备分布筛选项'

const shouldCacheDistributionResponse = (
    response: DeviceDistributionResponse
): boolean => response.meta.stats_degraded !== true

const shouldCacheDistributionOptionsResponse = (
    response: DeviceDistributionOptionsResponse
): boolean => response.stats_degraded !== true

const normalizeViewportNumber = (value: number | undefined): number | null => {
    if (value === undefined || value === null || !Number.isFinite(value)) {
        return null
    }

    const factor = 10 ** DISTRIBUTION_VIEWPORT_PRECISION
    return Math.round(value * factor) / factor
}

const buildDistributionCacheKey = (query: DeviceDistributionQuery): string =>
    JSON.stringify({
        minLng: normalizeViewportNumber(query.minLng),
        maxLng: normalizeViewportNumber(query.maxLng),
        minLat: normalizeViewportNumber(query.minLat),
        maxLat: normalizeViewportNumber(query.maxLat),
        zoom: query.zoom ?? null,
        projectIds: query.projectIds ?? [],
        regionIds: query.regionIds ?? [],
        deviceIds: query.deviceIds ?? [],
        pestTypes: query.pestTypes ?? [],
    })

const buildDeviceDetectionCacheKey = (
    deviceId: string,
    page: number,
    pageSize: number,
    filters?: DeviceDetectionFilters
): string =>
    JSON.stringify({
        deviceId,
        page,
        pageSize,
        startTime: filters?.startTime ?? null,
        endTime: filters?.endTime ?? null,
        pestTypes: filters?.pestTypes ?? [],
        countMode: filters?.countMode ?? null,
    })

export const clearDeviceMosquitoCache = (): void => {
    cachedRecords = null
    lastFetchTime = 0
    distributionCache.clear()
    distributionRequests.clear()
    deviceDetectionRequests.clear()
    cachedDistributionOptions = null
    distributionOptionsFetchedAt = 0
    distributionOptionsRequest = null
    distributionOptionsForceRequest = null
}

export const getRecords = async (
    forceRefresh = false
): Promise<DeviceMosquito[]> => {
    try {
        if (
            !forceRefresh &&
            cachedRecords &&
            Date.now() - lastFetchTime < CACHE_TTL
        ) {
            return cachedRecords
        }

        const response = await api.get<DeviceMosquito[]>(
            '/device-mosquito/get-device-stat'
        )

        cachedRecords = response.data
        lastFetchTime = Date.now()
        return response.data
    } catch (error) {
        console.error('获取设备分布记录失败:', error)
        return []
    }
}

export const getDeviceDetections = async (
    deviceId: string,
    page: number = 1,
    pageSize: number = 10,
    filters?: DeviceDetectionFilters,
    forceRefresh = false,
    options?: { signal?: AbortSignal }
): Promise<PaginatedDetectionResponse> => {
    try {
        const cacheKey = buildDeviceDetectionCacheKey(
            deviceId,
            page,
            pageSize,
            filters
        )
        const requestKey = `${cacheKey}${forceRefresh ? ':force' : ''}`
        const pendingRequest = deviceDetectionRequests.get(requestKey)
        if (pendingRequest) {
            return pendingRequest
        }

        const request = api
            .get<PaginatedDetectionResponse>(
                `/device-mosquito/devices/${deviceId}/detections`,
                {
                    params: {
                        page,
                        page_size: pageSize,
                        startTime: filters?.startTime,
                        endTime: filters?.endTime,
                        pestTypes:
                            filters?.pestTypes && filters.pestTypes.length > 0
                                ? filters.pestTypes.join(',')
                                : undefined,
                        countMode: filters?.countMode,
                    },
                    signal: options?.signal,
                    timeout: 15000,
                }
            )
            .then((response) => response.data)
            .finally(() => {
                if (deviceDetectionRequests.get(requestKey) === request) {
                    deviceDetectionRequests.delete(requestKey)
                }
            })
        deviceDetectionRequests.set(requestKey, request)
        return request
    } catch (error) {
        console.error('获取设备历史记录失败:', error)
        throw error
    }
}

export const getDistribution = async (
    query: DeviceDistributionQuery,
    forceRefresh = false,
    options?: { signal?: AbortSignal }
): Promise<DeviceDistributionResponse> => {
    const cacheKey = buildDistributionCacheKey(query)
    const requestKey = `${cacheKey}${forceRefresh ? ':force' : ''}`
    const cached = distributionCache.get(cacheKey)
    if (
        !forceRefresh &&
        cached &&
        Date.now() - cached.fetchedAt < CACHE_TTL
    ) {
        return cached.data
    }
    const existingRequest = distributionRequests.get(requestKey)
    if (existingRequest) {
        return existingRequest
    }

    const request = api
        .get<DeviceDistributionResponse>('/device-mosquito/distribution', {
            params: {
                minLng: query.minLng,
                maxLng: query.maxLng,
                minLat: query.minLat,
                maxLat: query.maxLat,
                zoom: query.zoom,
                projectIds:
                    query.projectIds && query.projectIds.length > 0
                        ? query.projectIds.join(',')
                        : undefined,
                regionIds:
                    query.regionIds && query.regionIds.length > 0
                        ? query.regionIds.join(',')
                        : undefined,
                deviceIds:
                    query.deviceIds && query.deviceIds.length > 0
                        ? query.deviceIds.join(',')
                        : undefined,
                pestTypes:
                    query.pestTypes && query.pestTypes.length > 0
                        ? query.pestTypes.join(',')
                        : undefined,
            },
            signal: options?.signal,
        })
        .then((response) => {
            if (shouldCacheDistributionResponse(response.data)) {
                distributionCache.set(cacheKey, {
                    data: response.data,
                    fetchedAt: Date.now(),
                })
            }
            return response.data
        })
        .finally(() => {
            if (distributionRequests.get(requestKey) === request) {
                distributionRequests.delete(requestKey)
            }
        })
    distributionRequests.set(requestKey, request)
    return request
}

export const getDistributionOptions = async (
    forceRefresh = false
): Promise<DeviceDistributionOptionsResponse> => {
    if (
        !forceRefresh &&
        cachedDistributionOptions &&
        Date.now() - distributionOptionsFetchedAt < CACHE_TTL
    ) {
        return cachedDistributionOptions
    }
    const existingRequest = forceRefresh
        ? distributionOptionsForceRequest
        : distributionOptionsRequest
    if (existingRequest) {
        return existingRequest
    }

    const request = api
        .get<DeviceDistributionOptionsResponse>(
            '/device-mosquito/distribution/options'
        )
        .then((response) => {
            if (shouldCacheDistributionOptionsResponse(response.data)) {
                cachedDistributionOptions = response.data
                distributionOptionsFetchedAt = Date.now()
            }
            return response.data
        })
        .finally(() => {
            if (forceRefresh) {
                if (distributionOptionsForceRequest === request) {
                    distributionOptionsForceRequest = null
                }
            } else if (distributionOptionsRequest === request) {
                distributionOptionsRequest = null
            }
        })
    if (forceRefresh) {
        distributionOptionsForceRequest = request
    } else {
        distributionOptionsRequest = request
    }
    return request
}

export const useData = () => {
    const [records, setRecords] = useState<DeviceMosquito[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchRecords = useCallback(async (forceRefresh = false) => {
        setLoading(true)
        setError(null)
        try {
            const data = await getRecords(forceRefresh)
            setRecords(data)
        } catch (nextError) {
            console.error('获取设备分布记录失败:', nextError)
            setError('无法加载设备分布记录')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void fetchRecords(false)
    }, [fetchRecords])

    return {
        records,
        loading,
        error,
        refreshRecords: fetchRecords,
    }
}

const getCachedDistributionOptionsSnapshot = ():
    | DeviceDistributionOptionsResponse
    | null => {
    if (
        cachedDistributionOptions &&
        Date.now() - distributionOptionsFetchedAt < CACHE_TTL
    ) {
        return cachedDistributionOptions
    }

    return null
}

export const useDistributionData = () => {
    const [distribution, setDistribution] =
        useState<DeviceDistributionResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isStatsRebuilding, setIsStatsRebuilding] = useState(false)
    const requestIdRef = useRef(0)
    const requestControllerRef = useRef<AbortController | null>(null)

    const fetchDistribution = useCallback(
        async (query: DeviceDistributionQuery, forceRefresh = false) => {
            requestControllerRef.current?.abort()
            const controller = new AbortController()
            requestControllerRef.current = controller
            requestIdRef.current += 1
            const requestId = requestIdRef.current
            setLoading(true)
            setError(null)
            try {
                const data = await getDistribution(query, forceRefresh, {
                    signal: controller.signal,
                })
                if (requestId !== requestIdRef.current) {
                    return data
                }
                setDistribution(data)
                setIsStatsRebuilding(false)
                return data
            } catch (nextError) {
                if (
                    requestId !== requestIdRef.current ||
                    controller.signal.aborted
                ) {
                    return null
                }
                console.error('获取设备分布数据失败:', nextError)
                setError(
                    isDeviceDistributionStatsRebuildingError(nextError)
                        ? null
                        : getDeviceDistributionErrorMessage(nextError)
                )
                setIsStatsRebuilding(false)
                return null
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false)
                }
                if (requestControllerRef.current === controller) {
                    requestControllerRef.current = null
                }
            }
        },
        []
    )

    useEffect(
        () => () => {
            requestControllerRef.current?.abort()
        },
        []
    )

    return {
        distribution,
        loading,
        error,
        isStatsRebuilding,
        fetchDistribution,
    }
}

export const useDistributionOptions = (
    config: UseDistributionOptionsConfig = {}
) => {
    const { initialForceRefresh = false, initialLoad = true } = config
    const [options, setOptions] =
        useState<DeviceDistributionOptionsResponse | null>(
            () => getCachedDistributionOptionsSnapshot()
        )
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isStatsRebuilding, setIsStatsRebuilding] = useState(false)
    const requestIdRef = useRef(0)

    const fetchOptions = useCallback(async (forceRefresh = false) => {
        requestIdRef.current += 1
        const requestId = requestIdRef.current
        setLoading(true)
        setError(null)
        try {
            const data = await getDistributionOptions(forceRefresh)
            if (requestId !== requestIdRef.current) {
                return data
            }
            setOptions(data)
            setIsStatsRebuilding(false)
            return data
        } catch (nextError) {
            if (requestId !== requestIdRef.current) {
                return null
            }
            console.error('获取设备分布筛选项失败:', nextError)
            setError(
                isDeviceDistributionStatsRebuildingError(nextError)
                    ? null
                    : getDeviceDistributionOptionsErrorMessage(nextError)
            )
            setIsStatsRebuilding(false)
            return null
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false)
            }
        }
    }, [])

    useEffect(() => {
        if (!initialLoad) {
            return
        }

        void fetchOptions(initialForceRefresh)
    }, [fetchOptions, initialForceRefresh, initialLoad])

    return {
        options,
        loading,
        error,
        isStatsRebuilding,
        refreshOptions: fetchOptions,
    }
}
